import { useAddContentActions } from 'components/WorkspaceEditor/hooks/useAddContentActions'
import { DataNode } from 'components/WorkspaceEditor/types/types'
import { useEffect, useRef } from 'react'
import { useNodeByContentId, useNodeMapShallowSelector } from '..'
import { PendingOperation, useAddContentAfterActionProps } from '../types'
import {
    cleanupExpiredOperations,
    createPendingOperation,
    detectMapChanges,
    getFirstChangedNode,
    isAnonymousTemporaryContent,
    shouldTriggerContentAddition,
} from '../utils/contentAction.utils'
import { EDITOR_OPERATION_DELAY_MSEC } from './useKeyOperation'

const OPERATION_TIMEOUT_MS = 10000 // 10 seconds to wait for new nodes

// Global storage for pending operations to survive component re-renders
const globalPendingOperations = new Map<string, PendingOperation>()

/**
 * useAddContentAfterAction hook handles adding content after a sibling node.
 * It supports both reactive and immediate strategies based on the content type.
 * - Reactive: Waits for map changes to trigger content addition
 * - Immediate: Triggers content addition immediately with a delay
 * Automatically chooses the right approach based on content type:
 * - Anonymous temporary content: Uses reactive strategy (waits for map changes)
 * - Regular content: Uses immediate strategy (direct call with delay)
 * @param {useAddContentAfterActionProps} props - The properties for the hook.
 * @param {boolean} props.shouldTriggerNewContent - Flag to determine if new content should
 * be added.
 * @param {boolean} props.fromHandleKeyDown - Flag to indicate if the action is
 * triggered from a key down event.
 * @param {string | undefined} props.contentItemId - The ID of the content item.
 * @param {(v: boolean) => void} props.setShouldTriggerNewContent - Function
 * to set the flag for triggering new content.
 * @param {(v: boolean) => void} props.setFromHandleKeyDown - Function
 * to set the flag for handling key down events.
 * @returns {void}
 */
export const useAddContentAfterAction = ({
    shouldTriggerNewContent,
    fromHandleKeyDown,
    contentItemId,
    setShouldTriggerNewContent,
    setFromHandleKeyDown,
}: useAddContentAfterActionProps) => {
    const {
        pendingContentItemIdForAddContent,
        setPendingContentItemIdForAddContent,
    } = useNodeMapShallowSelector((state) => ({
        pendingContentItemIdForAddContent:
            state.pendingContentItemIdForAddContent,
        setPendingContentItemIdForAddContent:
            state.setPendingContentItemIdForAddContent,
    }))

    // Try pending contentItemId first, if no node found, fall back to prop contentItemId
    const parentNode = useNodeByContentId(
        pendingContentItemIdForAddContent ?? contentItemId ?? ''
    )
    const effectiveContentItemId =
        pendingContentItemIdForAddContent ?? contentItemId ?? ''

    // Find the actual child node that contains this contentItemId
    // For UPDATE operations: parentNode is Article, we need the Paragraph child
    // For ADD operations: might not find child, fallback to parentNode
    const actualSiblingNode =
        parentNode?.children?.find((child) =>
            child.contentItemNodes?.some(
                (contentItem) => contentItem.id === effectiveContentItemId
            )
        ) ?? parentNode

    // Determine which strategy to use based on content type
    // If we have pendingContentItemIdForAddContent, use reactive strategy to wait for map update
    const isAnonymousContent = isAnonymousTemporaryContent(parentNode)
    const hasPendingContentItemId = !!pendingContentItemIdForAddContent

    const strategy =
        isAnonymousContent || hasPendingContentItemId ? 'reactive' : 'immediate'

    const addContentActions = useAddContentActions()
    const { contentItemToNodeMap } = useNodeMapShallowSelector((state) => ({
        contentItemToNodeMap: state.contentItemToNodeMap,
    }))
    const previousMapRef = useRef<Map<string, DataNode>>(new Map())

    useEffect(() => {
        if (strategy !== 'immediate') return

        const shouldAddNewContentToNode = shouldTriggerContentAddition(
            shouldTriggerNewContent,
            fromHandleKeyDown
        )

        if (!shouldAddNewContentToNode) return

        if (!actualSiblingNode) {
            setShouldTriggerNewContent(false)
            setFromHandleKeyDown(false)
            return
        }

        const timeoutId = setTimeout(() => {
            // Check canAddContent right before adding (after save operation completes)
            const canAdd = addContentActions.canAddContent(
                actualSiblingNode,
                'KeyboardShortcut'
            )

            if (!canAdd) {
                setShouldTriggerNewContent(false)
                setFromHandleKeyDown(false)
                setPendingContentItemIdForAddContent(null)
                return
            }

            addContentActions.addContentAfterSibling(actualSiblingNode)
            setShouldTriggerNewContent(false)
            setFromHandleKeyDown(false)
            setPendingContentItemIdForAddContent(null)
        }, EDITOR_OPERATION_DELAY_MSEC)

        return () => clearTimeout(timeoutId)
    }, [
        strategy,
        shouldTriggerNewContent,
        fromHandleKeyDown,
        addContentActions,
        actualSiblingNode,
        setShouldTriggerNewContent,
        setFromHandleKeyDown,
        setPendingContentItemIdForAddContent,
    ])

    // Step 1: Create pending strategy when triggered
    useEffect(() => {
        if (strategy !== 'reactive') return

        const shouldAddNewContentToNode = shouldTriggerContentAddition(
            shouldTriggerNewContent,
            fromHandleKeyDown
        )

        if (!shouldAddNewContentToNode) {
            return
        }

        // Create pending operation for the current node
        if (parentNode) {
            const operation = createPendingOperation(parentNode)
            globalPendingOperations.set(operation.id, operation)
        }
    }, [
        strategy,
        shouldTriggerNewContent,
        fromHandleKeyDown,
        contentItemId,
        parentNode,
    ])

    // Step 2: Detect map changes and execute action
    useEffect(() => {
        // Clean up expired operations first
        cleanupExpiredOperations(globalPendingOperations, OPERATION_TIMEOUT_MS)

        const pendingCount = globalPendingOperations.size

        // Only run if there are pending operations (user pressed Enter)
        if (pendingCount === 0) {
            // Always update the previous map reference even when not processing
            previousMapRef.current = new Map(contentItemToNodeMap)
            return
        }

        const currentMap = contentItemToNodeMap
        const previousMap = previousMapRef.current

        // Detect changes in the map
        const changes = detectMapChanges(currentMap, previousMap)

        // Always update the previous map reference for next comparison
        previousMapRef.current = new Map(currentMap)

        if (changes.length > 0) {
            // If we have pendingContentItemIdForAddContent, find the specific node with that ID
            // Otherwise, use the generic getFirstChangedNode
            let changedNode: DataNode | null = null

            if (pendingContentItemIdForAddContent) {
                // Find the node that contains this contentItemId
                changedNode =
                    currentMap.get(pendingContentItemIdForAddContent) || null
            } else {
                // Fallback to generic logic for anonymous content
                changedNode = getFirstChangedNode(changes)
            }

            if (changedNode) {
                addContentActions.addContentAfterSibling(changedNode)

                // Clear pending operations and flags when successfully processed
                globalPendingOperations.clear()
                setShouldTriggerNewContent(false)
                setFromHandleKeyDown(false)
                setPendingContentItemIdForAddContent(null)
            }
        }
    }, [
        contentItemToNodeMap,
        setShouldTriggerNewContent,
        setFromHandleKeyDown,
        addContentActions,
        setPendingContentItemIdForAddContent,
    ])
}
